<script setup lang="ts">
import type { ExclusivePropertyCardEmits, ExclusivePropertyCardProps } from '.';

import { useEvents } from '~/composables/events/useEvents';
import { getPropertyDetailsPath } from '~/composables/shorts';
import { useListingEventTracking } from '~/composables/useListingEventTracking';
import { useToggleFavoriteSync } from '~/composables/useToggleFavoriteSync';
import { getAreaLabel } from '~/core/entities/SystemEnums';
import { MixpanelProps } from '~/core/service/mixpanel';
import { cn, floatToLocaleString, hasShortsVideo } from '~/utils';
import { grammarInflection } from '~~/utils';

import { CardCarousel } from '~/components/Card/CardCarousel';
import CardCarouselImage from '~/components/Card/CardCarouselImage/CarouselImage.vue';
import { FavoriteButton } from '~/components/FavoriteButton';
import { PropertyCardItemSeparator } from '~/components/PropertyCard';
import PropertyCardStatusBadges from '~/components/PropertyCard/PropertyCardStatusBadges.vue';
import { Button } from '~/components/ui/button';

import IconMaterialSymbolsSlowMotionVideo from '~icons/material-symbols/slow-motion-video-sharp';

const props = defineProps<ExclusivePropertyCardProps>();
const emit = defineEmits<ExclusivePropertyCardEmits>();

const route = useRoute();
const { trackPropertyCardClicked } = useEvents();
const { trackClick } = useListingEventTracking();
const { loggedIn: isUserLoggedIn } = useUserSession();

const isMouseOverCard = ref(false);

/**
 * O card ocupa a largura inteira da coluna do perfil — cerca de 100vw até o
 * `md` e até ~1000px no desktop —, muito acima do que o padrão dos cards de
 * listagem pede ao provedor. O 2x cobre telas de alta densidade.
 */
const IMAGE_SIZES = 'xs:100vw sm:100vw md:100vw lg:800px xl:1000px xxl:1024px';
const IMAGE_DENSITIES = 'x1 x2';

/**
 * Fotos e cards são dois Embla aninhados: sem barrar a propagação, o gesto
 * sobre a foto também chega ao slider de cards e os dois andam juntos. Com uma
 * foto só não há o que paginar, e o gesto segue passando os cards.
 */
const hasPhotoCarousel = computed<boolean>(
	() => (props.property.images?.length ?? 0) > 1
);

function keepGestureInPhotoCarousel(event: Event): void {
	if (hasPhotoCarousel.value) event.stopPropagation();
}

/**
 * Sem vídeo publicado o feed abriria numa galeria de fotos, que não é o que o
 * botão promete — então ele simplesmente não é renderizado e o resto do card
 * continua igual.
 */
const hasPublishedVideo = computed<boolean>(() =>
	hasShortsVideo(props.property)
);

const detailsPath = computed<string>(() =>
	getPropertyDetailsPath(props.property.commercialId, props.property.slug)
);

const title = computed<string>(() => {
	const propertyTypeName = props.property.propertyType?.name?.trim();
	const region = props.property.region?.trim();

	return [propertyTypeName, region].filter(Boolean).join(' em ');
});

const locationLabel = computed<string>(() =>
	[props.property.city?.trim(), props.property.state?.trim()]
		.filter(Boolean)
		.join(' - ')
);

/**
 * O mapper devolve `null` quando o imóvel não tem preço cadastrado, e o
 * formatador transformaria isso em "R$ 0". Como o critério manda ocultar o
 * elemento sem dado, o preço só existe quando há valor.
 */
const formattedAskingPrice = computed<string | null>(() =>
	props.property.askingPrice
		? floatToLocaleString(props.property.askingPrice)
		: null
);

const formattedArea = computed<string>(() =>
	floatToLocaleString(props.property.area, { style: 'decimal' })
);

/**
 * O tipo declara `propertyType` como obrigatório, mas o mapper devolve
 * `undefined` quando não consegue inferi-lo do slug, então o acesso é
 * protegido para o card não derrubar a página no SSR.
 */
const areaLabel = computed<string>(() =>
	getAreaLabel(props.property.propertyType?.identifier)
);

/**
 * Os dots ficam centralizados no card: acima do card de informações no mobile,
 * onde ele cobre a base da foto, e na base da foto a partir do tablet. O
 * deslocamento no mobile acompanha a altura do card de informações, que é fixa
 * porque título e atributos ocupam uma linha só. Tablet não tem hover, então
 * lá os dots ficam sempre visíveis.
 */
const dotsVariants = computed(
	() => () =>
		cn(
			'absolute inset-x-0 bottom-[14.5rem] z-20 mx-auto flex h-5 w-fit gap-2 rounded-full bg-primary/40 px-3 py-2 transition-opacity delay-300 ease-in-out',
			'md:bottom-4 md:opacity-100 lg:opacity-0'
		)
);

const trackingSharedProps = computed(() => ({
	[MixpanelProps.COMMERCIAL_ID]: props.property.commercialId,
	[MixpanelProps.CURRENT_PAGE]: route.fullPath,
	[MixpanelProps.SECTION_NAME]: props.section || '',
	[MixpanelProps.CARD_POSITION]: props.cardPosition,
	[MixpanelProps.CARD_TYPE]: 'full face',
	[MixpanelProps.COMPANY_NAME]: props.property.company?.name,
	[MixpanelProps.TAG_EXCLUSIVE]: props.property.isExclusive,
	[MixpanelProps.AGENT_NAME]: props.property.agent?.name
}));

/**
 * Favoritar segue o mesmo contrato do `PropertyCard`: o botão é apresentacional
 * e quem resolve estado, visitante deslogado e atualização otimista é o
 * composable. Sem ele o clique não chegaria à wishlist e um imóvel já salvo
 * apareceria desmarcado.
 */
const favoriteEventData = computed(() => ({
	currentPage: route.fullPath,
	tagExclusive: props.property.isExclusive,
	companyName: props.property.company?.name,
	agentName: props.property.agent?.name,
	clickCard: true,
	videoShorts: hasPublishedVideo.value
}));

const { isFavorite, handleToggleFavorite, isValidData } = useToggleFavoriteSync(
	() => props.property.id,
	() => props.property.commercialId,
	favoriteEventData,
	() => props.section,
	() => props.property.inUserWishlist ?? false,
	{ property: () => props.property }
);

/**
 * Mesmo par de eventos que o `PropertyCard` dispara: o de produto no Mixpanel
 * e o de listagem que alimenta a recomendação. Sem isso o clique neste card
 * seria invisível para as duas pontas.
 */
function handleDetailsClick(): void {
	emit('details-click');

	const clickedAt = new Date().toISOString();

	trackPropertyCardClicked({
		[MixpanelProps.DESTINATION_URL]: detailsPath.value,
		...trackingSharedProps.value
	});

	if (!props.trackingContext || typeof props.positionInList !== 'number') {
		return;
	}

	trackClick({
		propertyId: props.property.id,
		clickedAt,
		context: {
			...props.trackingContext,
			url: route.path,
			position: props.positionInList + 1
		}
	});
}

/**
 * Links e botões do card já têm destino próprio; o clique no resto dele leva à
 * mesma PDP do "Ver detalhes". Clique ao fim de um arraste não chega aqui,
 * porque o Embla o cancela na captura.
 */
function handleCardClick(event: MouseEvent): void {
	const originatedInAction = event
		.composedPath()
		.some(target => target instanceof Element && target.matches('a, button'));

	if (originatedInAction) return;

	handleDetailsClick();
	void navigateTo(detailsPath.value);
}

function handleExploreVideoClick(): void {
	emit('explore-video', props.property);
}

function getImageAlt(index: number): string {
	return getPropertyImageAlt(
		props.property.propertyType?.name,
		props.property.suites,
		props.property.region,
		props.property.city,
		props.property.askingPrice,
		index
	);
}
</script>

<template>
	<article
		:class="
			cn(
				'relative h-[440px] w-full cursor-pointer overflow-hidden md:h-[400px] xl:h-[460px]',
				props.class
			)
		"
		data-testid="exclusive-property-card"
		@click="handleCardClick"
		@mouseover="isMouseOverCard = true"
		@mouseleave="isMouseOverCard = false"
	>
		<div
			class="group/card absolute inset-0"
			data-testid="exclusive-property-card-photos"
			@pointerdown="keepGestureInPhotoCarousel"
			@touchstart.passive="keepGestureInPhotoCarousel"
			@mousedown="keepGestureInPhotoCarousel"
			@wheel.passive="keepGestureInPhotoCarousel"
		>
			<CardCarousel
				variant="fullFace"
				size="lg"
				:images="property.images"
				:is-mouse-over-card-header="isMouseOverCard"
				:dots-variants="dotsVariants"
			>
				<template #default="{ image, index }">
					<CardCarouselImage
						:index="index"
						:image="image"
						variant="fullFace"
						:image-alt="getImageAlt(index)"
						:is-property-active="true"
						:card-position="props.cardPosition"
						:sizes="IMAGE_SIZES"
						:densities="IMAGE_DENSITIES"
					/>
				</template>
			</CardCarousel>
		</div>

		<PropertyCardStatusBadges
			:property="property"
			:is-property-active="true"
			:is-exclusive="true"
			class="md:left-6 md:right-18 md:top-6"
		/>

		<div
			v-if="isValidData"
			class="absolute right-4 top-4 z-30 md:right-6 md:top-6"
			@click.stop
		>
			<ClientOnly>
				<template #fallback>
					<FavoriteButton
						:is-favorite="false"
						:property-id="property.id"
						:commercial-id="property.commercialId"
					/>
				</template>
				<FavoriteButton
					:key="`favorite-${property.id}-${isUserLoggedIn}`"
					v-model:is-favorite="isFavorite"
					:property-id="property.id"
					:commercial-id="property.commercialId"
					@toggle="handleToggleFavorite"
				/>
			</ClientOnly>
		</div>

		<div
			class="absolute inset-x-3 bottom-3 z-20 flex flex-col gap-3 bg-white p-4 md:inset-x-auto md:bottom-12 md:right-12 md:w-[380px] md:gap-4 md:px-7 md:py-6 lg:right-16 xl:bottom-14 xl:right-18 xl:px-8 xl:py-7"
			data-testid="exclusive-property-card-info"
		>
			<div class="flex flex-col gap-0.5">
				<h3
					class="truncate font-matter-sq text-lg font-normal leading-[1.5] tracking-[-0.02em] text-primary xl:text-xl"
				>
					{{ title }}
				</h3>

				<div
					class="flex flex-nowrap items-center gap-2 text-xs font-normal leading-[1.5] text-primary-light-1"
				>
					<PropertyCardItemSeparator v-if="locationLabel" class="truncate">
						<span class="md:text-sm">{{ locationLabel }}</span>
					</PropertyCardItemSeparator>
					<PropertyCardItemSeparator v-if="property.commercialId">
						{{ property.commercialId }}
					</PropertyCardItemSeparator>
				</div>
			</div>

			<hr class="border-white-dark-2" />

			<div class="flex flex-col gap-4 md:gap-5">
				<div class="flex flex-col gap-0.5">
					<p
						v-if="formattedAskingPrice"
						class="font-matter-sq text-xl font-normal leading-[1.5] tracking-[-0.02em] text-primary xl:text-2xl"
					>
						{{ formattedAskingPrice }}
					</p>

					<div
						class="flex flex-nowrap items-center gap-2 text-xs font-normal leading-[1.5] text-primary md:text-sm"
					>
						<PropertyCardItemSeparator v-if="property.area" class="truncate">
							{{ formattedArea }}m²
							<span class="lowercase">{{ areaLabel }}</span>
						</PropertyCardItemSeparator>
						<PropertyCardItemSeparator v-if="property.suites">
							{{ property.suites }}
							{{ grammarInflection(property.suites, 'suíte', 'suítes') }}
						</PropertyCardItemSeparator>
						<PropertyCardItemSeparator
							v-else-if="property.bedrooms"
							class="truncate"
						>
							{{ property.bedrooms }}
							{{ grammarInflection(property.bedrooms, 'quarto', 'quartos') }}
						</PropertyCardItemSeparator>
						<PropertyCardItemSeparator v-if="property.parkingSpots">
							{{ property.parkingSpots }}
							{{ grammarInflection(property.parkingSpots, 'vaga', 'vagas') }}
						</PropertyCardItemSeparator>
					</div>
				</div>

				<div class="flex items-center gap-4 md:gap-5">
					<Button
						as-child
						variant="secondary"
						size="xs"
						class="w-fit md:h-10 md:gap-2 md:text-sm"
						data-testid="exclusive-property-card-details"
					>
						<NuxtLink :to="detailsPath" @click.stop="handleDetailsClick">
							Ver detalhes
						</NuxtLink>
					</Button>

					<Button
						v-if="hasPublishedVideo"
						variant="transparent"
						size="xs"
						class="group w-fit gap-1.5 px-0 md:text-sm"
						data-testid="exclusive-property-card-explore-video"
						@click.stop="handleExploreVideoClick"
					>
						<IconMaterialSymbolsSlowMotionVideo
							class="size-5 shrink-0"
							aria-hidden="true"
						/>
						<span
							class="underline decoration-white-dark-4 underline-offset-4 transition-colors group-hover:decoration-primary"
						>
							Explorar em vídeo
						</span>
					</Button>
				</div>
			</div>
		</div>
	</article>
</template>
